import React from 'react';
import { PLANNING_CATEGORY_ARTWORK } from '../../../lib/planning/categoryArtwork';

type CategoryIconProps = {
  categoryId: string;
  size?: number;
  className?: string;
};

const strokeProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const rasterIcons: Record<string, string> = { ...PLANNING_CATEGORY_ARTWORK };

const icons: Record<string, React.ReactNode> = {
  change_of_use_conversions: (
    <>
      <rect {...strokeProps} x="5" y="5" width="14" height="14" rx="1" />
      <path {...strokeProps} d="M9 12h6M12 9v6" />
    </>
  ),
  short_term_lets: (
    <>
      <path {...strokeProps} d="M4 10h16v10H4z" />
      <path {...strokeProps} d="M8 10V7a4 4 0 0 1 8 0v3" />
      <circle {...strokeProps} cx="12" cy="15" r="1.5" />
    </>
  ),
  extensions_enlargements: (
    <>
      <path {...strokeProps} d="M4 20h10V10h6v10h-4" />
      <path {...strokeProps} d="M14 10V6h6v4" />
    </>
  ),
  demolition: (
    <>
      <path {...strokeProps} d="M3 20h18M8 20l2-8h4l2 8" />
      <path {...strokeProps} d="M10 12V8h4v4M7 8h10" />
    </>
  ),
  outbuildings_ancillary: (
    <>
      <path {...strokeProps} d="M4 20h16M8 20V12h8v8" />
      <path {...strokeProps} d="M6 12l6-5 6 5" />
    </>
  ),
  windows_doors_glazing: (
    <>
      <rect {...strokeProps} x="5" y="5" width="14" height="14" rx="1" />
      <path {...strokeProps} d="M12 5v14M5 12h14" />
    </>
  ),
  roof_works_dormers: (
    <>
      <path {...strokeProps} d="M3 14l9-7 9 7v6H3z" />
      <rect {...strokeProps} x="10" y="12" width="4" height="4" />
    </>
  ),
  mechanical_services: (
    <>
      <circle {...strokeProps} cx="12" cy="12" r="3" />
      <path {...strokeProps} d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
    </>
  ),
  renewables_energy: (
    <>
      <circle {...strokeProps} cx="12" cy="12" r="4" />
      <path {...strokeProps} d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3" />
    </>
  ),
  landscaping_boundaries: (
    <>
      <path {...strokeProps} d="M4 20c2-6 4-10 8-10s6 4 8 10" />
      <path {...strokeProps} d="M4 20h16" />
    </>
  ),
  parking_access: (
    <>
      <rect {...strokeProps} x="4" y="8" width="16" height="10" rx="1" />
      <path {...strokeProps} d="M8 18v2M16 18v2M8 12h3a2 2 0 1 0 0-4H8v6" />
    </>
  ),
  external_lighting: (
    <>
      <path {...strokeProps} d="M12 3v2M8.5 5.5l1.4 1.4M15.5 5.5l-1.4 1.4" />
      <path {...strokeProps} d="M9 14h6l-1 6h-4zM10 10h4v4h-4z" />
    </>
  ),
  trees_ecology: (
    <>
      <path {...strokeProps} d="M12 20V12M12 12c-3-2-5-4-5-7a5 5 0 0 1 10 0c0 3-2 5-5 7z" />
    </>
  ),
  legal_admin: (
    <>
      <path {...strokeProps} d="M7 4h10v16H7z" />
      <path {...strokeProps} d="M10 8h4M10 12h4M10 16h2" />
    </>
  ),
  interiors_accessibility: (
    <>
      <path {...strokeProps} d="M6 20V8h12v12" />
      <path {...strokeProps} d="M6 12h12M10 8V5h4v3" />
    </>
  ),
  shopfront_signage: (
    <>
      <path {...strokeProps} d="M4 10h16v8H4zM6 10V6h12v4" />
      <path {...strokeProps} d="M9 14h6" />
    </>
  ),
  public_realm_community: (
    <>
      <circle {...strokeProps} cx="8" cy="8" r="2" />
      <circle {...strokeProps} cx="16" cy="8" r="2" />
      <path {...strokeProps} d="M4 20c0-3 2-5 4-5s4 2 4 5M12 20c0-3 2-5 4-5s4 2 4 5" />
    </>
  ),
  telecommunications: (
    <>
      <path {...strokeProps} d="M12 20V8M8 12l4-4 4 4" />
      <path {...strokeProps} d="M5 16a7 7 0 0 1 14 0" />
    </>
  ),
};

const fallbackIcon = (
  <>
    <rect {...strokeProps} x="6" y="6" width="12" height="12" rx="1" />
    <path {...strokeProps} d="M9 12h6" />
  </>
);

export const CategoryIcon: React.FC<CategoryIconProps> = ({ categoryId, size = 20, className = '' }) => {
  const rasterIcon = rasterIcons[categoryId];

  if (rasterIcon) {
    return (
      <img
        src={rasterIcon}
        alt=""
        aria-hidden="true"
        draggable={false}
        width={size}
        height={size}
        className={['max-h-full max-w-full object-contain', className].filter(Boolean).join(' ')}
      />
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
    >
      {icons[categoryId] || fallbackIcon}
    </svg>
  );
};

export default CategoryIcon;
